import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { HealthV1Response } from '../lib/types';

type Status = 'loading' | 'ok' | 'warn' | 'bad';

/**
 * Badge minimalista en el header. Consume /v1/health (TK-S02-04) y
 * muestra el peor estado consolidado:
 *  - DB caída             → 'bad'  (rojo)
 *  - Adapters con ERROR   → 'bad'
 *  - Adapters con STALE   → 'warn' (amarillo)
 *  - Todo en orden        → 'ok'   (verde)
 *
 * El detalle por-adapter vive en el HealthDashboard del Sidebar.
 */
export function HealthBadge() {
  const [status, setStatus] = useState<Status>('loading');
  const [data, setData] = useState<HealthV1Response | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    let cancelled = false;
    const check = async () => {
      try {
        const h = await api.health(ctrl.signal);
        if (cancelled) return;
        setData(h);
        if (!h.db || h.summary.error > 0) setStatus('bad');
        else if (h.summary.stale > 0 || (h.summary.degraded ?? 0) > 0) setStatus('warn');
        else setStatus('ok');
      } catch {
        if (!cancelled) setStatus('bad');
      }
    };
    check();
    const id = setInterval(check, 60_000);
    return () => { cancelled = true; ctrl.abort(); clearInterval(id); };
  }, []);

  const degraded = data?.summary.degraded ?? 0;
  const label = status === 'loading' ? 'verificando…'
    : status === 'ok' ? 'operativo'
    : status === 'warn'
      ? (data && data.summary.stale > 0
          ? `${data.summary.stale} sin actualizar`
          : `${degraded} con volumen bajo`)
    : data && data.summary.error > 0 ? `${data.summary.error} con error`
    : 'caído';

  // Tooltip legible para stakeholders en vez del JSON crudo.
  const tooltip = data
    ? `Fuentes — OK: ${data.summary.ok} · volumen bajo: ${degraded} · sin actualizar: ${data.summary.stale} · con error: ${data.summary.error}` +
      `${(data.summary.manual ?? 0) > 0 ? ` · carga manual: ${data.summary.manual}` : ''}` +
      `\nBase de datos: ${data.db ? 'conectada' : 'sin conexión'}`
    : 'Consultando estado del sistema…';

  return (
    <div className="health-badge" title={tooltip}>
      <span className={`dot ${status === 'loading' ? '' : status}`} />
      <span>{label}</span>
    </div>
  );
}
